import { useNostr } from './useNostr';
import { useQuery } from '@tanstack/react-query';

/**
 * Hook to fetch the list of pubkeys who follow a given user (kind 3 events
 * with a p tag pointing to the target pubkey).
 *
 * Caveat: Not all relays index #p on kind 3, so followers count is
 * inherently approximate — same limitation as every Nostr client.
 */
export function useFollowersList(pubkey: string) {
  const { nostr } = useNostr();

  const { data: followersPubkeys = [], isLoading } = useQuery({
    queryKey: ['followers-list', pubkey],
    queryFn: async () => {
      if (!nostr) return [];

      const events = await nostr.query([
        { kinds: [3], '#p': [pubkey] },
      ]);

      // Each event pubkey is a follower — deduplicate by pubkey
      const seen = new Set<string>();
      const unique: string[] = [];
      for (const event of events) {
        if (!seen.has(event.pubkey)) {
          seen.add(event.pubkey);
          unique.push(event.pubkey);
        }
      }
      return unique;
    },
    enabled: !!pubkey && !!nostr,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  return {
    pubkeys: followersPubkeys, // string[] — list of follower pubkeys
    count: followersPubkeys.length,
    isLoading,
  };
}